import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { dreamDriftIsResurrectionLocked } from '../dreamDrift.utils';
import { dreamDriftSchedule } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import {
  dreamDriftTheme,
  type DreamDriftHudButtonAssets,
} from 'src/game/dreamDrift/dreamDrift.theme';
import { now, user } from 'src/StateObserver';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  dreamDriftGetInstanceRemainingTime,
  dreamDriftGetMapRemainingTime,
  dreamDriftHasRewardsAvailable,
  dreamDriftHasCompletedCurrentEvent,
  dreamDriftIsAvailable,
} from 'src/replicant/dreamDrift/dreamDrift.getters';

/** Home HUD entry button for an active Dream Drift event or reward claim. */
export class DreamDriftHudButton extends FeatureButton {
  /** Current progression level displayed over the icon's flag. */
  private readonly levelLabel: LangBitmapFontTextView;

  /** Static icon used by themes without a Spine export. */
  private readonly staticIcon: ImageScaleView;

  /** Asset description currently configured on this persistent HUD button. */
  private hudAssets?: DreamDriftHudButtonAssets;

  /**
   * Create the themed Dream Drift HUD button.
   *
   * @param opts Standard home-button view, tracking, and tap options.
   */
  constructor(opts: ButtonScaleViewOpts) {
    // Resolve the current theme's timer before constructing the view.
    const assets = dreamDriftTheme().getHudButtonAssets();

    // Build the standard home-button bounds and timer label panel.
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      lockedOverlay: {},
      labelPanel: {
        width: downscale(206),
        height: downscale(63),
        background: {
          image: assets.timer,
        },
      },
      ...opts,
    });

    // Offset the static artwork slightly left and down within the shared icon area.
    this.staticIcon = new ImageScaleView({
      superview: this.container,
      width: downscale(228),
      height: downscale(228),
      x: this.container.style.width / 2 - 2,
      y: this.container.style.height / 2 + 5,
      centerOnOrigin: true,
      zIndex: -1,
      visible: false,
    });

    // Center the current progression level inside the icon's flag.
    this.levelLabel = new LangBitmapFontTextView({
      superview: this.container,
      ...assets.levelLabelLayout,
      width: 32,
      height: 32,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      font: 'Body',
      size: 22,
      color: '#FFFFFF',
      visible: false,
      localeText: () => '',
      zIndex: 1,
    });

    // Center the countdown text vertically within the timer panel.
    this.labelPanel?.label.updateOpts({
      y: -2,
    });

    // Configure the first event's static or animated icon.
    this.updateTheme();
  }

  /** Refresh HUD artwork and number placement when this button enters a new event. */
  private updateTheme(): void {
    // Stable theme asset objects prevent repeated loads during the one-second HUD tick.
    const assets = dreamDriftTheme().getHudButtonAssets();
    if (assets === this.hudAssets) return;
    this.hudAssets = assets;

    // Keep the level number inside the active theme's flag after event rollover.
    this.levelLabel.updateOpts(assets.levelLabelLayout);

    // Discard the previous skeleton rather than reusing a different theme's export.
    this.spine?.removeFromSuperview();
    this.spine = undefined;
    this.staticIcon.hide();
    this.currentAnimationState = undefined;
    this.labelPanel?.updatePanelOpts({ background: { image: assets.timer } });

    // Static themes use FeatureButton's custom presentation without constructing Spine.
    if (assets.kind === 'static') {
      this.animationStateMap = {
        main: {
          type: 'custom',
          assetGroup: new AssetGroup([assets.image, assets.timer]),
          onEnter: () => {
            // Display the supplied static icon after its assets finish loading.
            this.staticIcon.setImage(assets.image);
            this.staticIcon.show();
          },
        },
      };
      return;
    }

    // Animated themes retain their looping HUD animation.
    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup([assets.spine, assets.timer]),
        url: assets.spine.url,
        scale: DOWNSCALE_FACTOR,
        sequence: ['icon_idle'],
      },
    };
  }

  /** Update visibility and countdown text from the current Dream Drift state. */
  public override update(): void {
    // Read one consistent state and timestamp for every update calculation.
    const state = user();
    const currentTime = now();

    // Retain the entry whenever the current instance has rewards to claim.
    const hasRewardsAvailable = dreamDriftHasRewardsAvailable(
      state,
      currentTime,
    );
    const isAvailable = dreamDriftIsAvailable(state, currentTime);
    const isLocked = dreamDriftIsResurrectionLocked();
    if (!isAvailable && !hasRewardsAvailable && !isLocked) {
      this.hide();
      return;
    }

    // Refresh persistent HUD artwork before displaying the current event.
    this.updateTheme();
    this.show();
    void this.setAnimationState('main');

    // Preview the ongoing event with the same tappable lock overlay as Cards.
    if (isLocked) {
      this.lock();
      this.updateOpts({ blockEvents: false });
      this.levelLabel.hide();
      this.setLabelTime({
        duration:
          (dreamDriftSchedule.getCurrentEvent(currentTime)?.end ??
            currentTime) - currentTime,
      });
      return;
    }

    // Remove the preview lock immediately after the player reaches the unlock level.
    if (this.isLocked) void this.unlock(true);

    // Replace the expired timer with an explicit claim label during the event gap.
    if (hasRewardsAvailable) {
      this.setLabelLocaleText('match2.tournament.claim');
      this.levelLabel.localeText = () => '';
      this.levelLabel.hide();
      return;
    }

    // Completed events disappear after their reward claim has closed.
    const hasCompletedCurrentEvent = dreamDriftHasCompletedCurrentEvent(
      state,
      currentTime,
    );
    if (hasCompletedCurrentEvent) {
      this.hide();
      return;
    }

    // Prefer an active instance timer, otherwise count down the event window.
    this.setLabelTime({
      duration: dreamDriftGetMapRemainingTime(state, currentTime),
    });

    // Leave the flag empty when there is no valid ongoing instance.
    const instance = state.dreamDrift?.instance;
    const instanceRemainingTime = dreamDriftGetInstanceRemainingTime(
      state,
      currentTime,
    );
    if (!instance || instanceRemainingTime === 0) {
      this.levelLabel.localeText = () => '';
      this.levelLabel.hide();
      return;
    }

    // Display the next progression level that the ongoing instance will play.
    const currentLevel = instance.progress + 1;
    this.levelLabel.localeText = () => `${currentLevel}`;
    this.levelLabel.show();
  }
}
